import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

// ===== utils =====
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }
function pow2(n) { return 1 << n; } // wystarczy dla n ≤ 30

// akceptuje: całkowite, a/b, dziesiętne (kropka/przecinek)
function parseFraction(raw) {
  const s0 = String(raw).trim();
  const s = s0.replace(",", ".");
  if (/^[+-]?\d+$/.test(s)) return [parseInt(s, 10), 1];
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) { const den = parseInt(m[2], 10); if (den === 0) return null; return [parseInt(m[1], 10), den]; }
  const d = s.match(/^[+-]?(?:\d+|\d*\.\d+)$/);
  if (d) {
    const sign = s.startsWith("-") ? -1 : 1;
    const body = s.replace(/^[+-]/, "");
    const [ip, fp = ""] = body.split(".");
    const k = fp.length;
    const p = sign * (parseInt(ip || "0", 10) * (10 ** k) + parseInt(fp || "0", 10));
    const q = 10 ** k;
    return [p, q];
  }
  return null;
}

function nCr(n, r) {
  if (r < 0 || r > n) return 0;
  r = Math.min(r, n - r);
  let num = 1, den = 1;
  for (let i = 1; i <= r; i++) { num *= (n - r + i); den *= i; }
  return Math.round(num / den);
}

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// ===== odmiana „orzeł” i czasownik =====
// 1 → „orzeł”; 2–4 (poza 12–14) → „orły”; reszta → „orłów”
function orzelForm(n) {
  const abs = Math.abs(n);
  const last2 = abs % 100;
  const last1 = abs % 10;
  if (abs === 1) return "orzeł";
  if (last2 >= 12 && last2 <= 14) return "orłów";
  if (last1 >= 2 && last1 <= 4) return "orły";
  return "orłów";
}

// czasownik: 1 oraz 5+ (i 12–14) → „wypadnie”; 2–4 (poza 12–14) → „wypadną”
function verbForCount(n) {
  const v = Math.abs(n) % 100;
  const u = v % 10;
  if (n === 1) return "wypadnie";
  if (u >= 2 && u <= 4 && !(v >= 12 && v <= 14)) return "wypadną";
  return "wypadnie";
}

// ===== tryby zdarzeń =====
const MODES = [
  {
    key: "EXACT",
    mkTitle: (n, r) => `dokładnie ${r} ${orzelForm(r)}`,
    mkLatex: (n, r) => `\\text{dokładnie } ${r}\\,\\text{ ${orzelForm(r)} }`,
  },
  {
    key: "AT_LEAST",
    mkTitle: (n, r) => `co najmniej ${r} ${orzelForm(r)}`,
    mkLatex: (n, r) => `\\ge ${r}\\,\\text{ ${orzelForm(r)} }`,
  },
  {
    key: "AT_MOST",
    mkTitle: (n, r) => `co najwyżej ${r} ${orzelForm(r)}`,
    mkLatex: (n, r) => `\\le ${r}\\,\\text{ ${orzelForm(r)} }`,
  },
  {
    key: "BETWEEN",
    mkTitle: (n, a, b) => `od ${a} do ${b} orłów`, // po "od...do..." dopełniacz
    mkLatex: (n, a, b) => `\\text{od } ${a} \\text{ do } ${b} \\text{ orłów}`,
  },
];

// ===== core =====
function generateOne() {
  const n = 6 + Math.floor(Math.random() * 7); // 6..12
  const mode = pick(MODES);

  // losujemy parametry
  let r = Math.floor(Math.random() * (n + 1));
  let a = 0, b = 0;
  if (mode.key === "BETWEEN") {
    a = Math.floor(Math.random() * (n + 1));
    b = Math.floor(Math.random() * (n + 1));
    if (a > b) [a, b] = [b, a];
  }
  if (mode.key === "AT_LEAST" || mode.key === "AT_MOST" || mode.key === "EXACT") {
    r = Math.max(1, Math.min(n - 1, r)); // bez trywialnych 0 i n
  }

  // liczność zdarzenia (num), lista składników bezpośredniej sumy (ks)
  const den = pow2(n);
  let num = 0;
  let ks = [];

  if (mode.key === "EXACT") {
    num = nCr(n, r);
    ks = [r];
  } else if (mode.key === "AT_LEAST") {
    for (let k = r; k <= n; k++) { num += nCr(n, k); ks.push(k); }
  } else if (mode.key === "AT_MOST") {
    for (let k = 0; k <= r; k++) { num += nCr(n, k); ks.push(k); }
  } else if (mode.key === "BETWEEN") {
    for (let k = a; k <= b; k++) { num += nCr(n, k); ks.push(k); }
  }

  const [p, q] = simplify(num, den);

  // tytuł i LaTeX
  let title, latex;
  if (mode.key === "BETWEEN") { title = mode.mkTitle(n, a, b); latex = mode.mkLatex(n, a, b); }
  else { title = mode.mkTitle(n, r); latex = mode.mkLatex(n, r); }

  return { n, den, num, p, q, modeKey: mode.key, r, a, b, ks, modeTitle: title, modeLatex: latex };
}

export function generateTask() {
  const d = generateOne();
  return {
    type: "CoinTossesTask",
    data: d,
    uid: `coin_${d.modeKey}_${d.n}_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", margin: "12px 0" }}>
      <div>
        <span>Rzucamy monetą </span>
        <InlineMath math={`${data.n}`} />
        <span> razy. Oblicz prawdopodobieństwo, że </span>
        <span>{data.modeKey === "BETWEEN" ? "wypadnie" : verbForCount(data.r)} </span>
        <b>{data.modeTitle}</b>
        <span>.</span>
      </div>
    </div>
  );
}

// input: P(A) = …
export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3">
      <span><InlineMath math={'P(A) = '} /></span>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="P(A)"
        placeholder="np. 163/256 lub 0.64"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return { correct: false, message: "Podaj ułamek (np. 3/10) lub liczbę (np. 0.3)." };
  }
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct: false, message: "Nieprawidłowy ułamek/liczba." };

  const [gp, gq] = parsed;
  const [cp, cq] = simplify(data.p, data.q);
  const [gpp, gqq] = simplify(gp, gq);

  let correct = (gpp === cp && gqq === cq);
  if (!correct) {
    const userVal = gp / gq;
    const correctVal = cp / cq;
    if (Math.abs(userVal - correctVal) <= 0.01 + 1e-12) correct = true;
  }
  return { correct, expected: `\\tfrac{${cp}}{${cq}}` };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={`P(A) = ${dfrac(data.p, data.q)}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={formatLatexPreview(userAnswer.raw, 'P(A)')} />
    </div>
  );
}

// ——— Wyjaśnienie (wybór krótszej prezentacji + wartości liczbowe) ———
export function renderExplanation({ data }) {
  const { n, den, num, modeKey, r, a, b, ks } = data;
  const [simpNum, simpDen] = simplify(num, den);
  const tail = (simpNum === num && simpDen === den) ? "" : `= \\dfrac{${simpNum}}{${simpDen}}`;

  const omegaLine = `\\Omega = \\{\\text{wszystkie możliwe ciągi wyników z } ${n} \\text{ rzutów}\\},\\; |\\Omega| = 2^{${n}} = ${den}`;

  // bezpośrednie sumy
  const directBinos = ks.map(k => `\\binom{${n}}{${k}}`).join(" + ");
  const directVals  = ks.map(k => nCr(n, k)).join(" + ");

  let aCountLatex = "";

  if (modeKey === "EXACT") {
    const val = nCr(n, r);
    aCountLatex = `|\\mathrm{A}| = \\binom{${n}}{${r}} = ${val}`;
  } else if (modeKey === "AT_LEAST") {
    const directLen = n - r + 1;
    const compKs = Array.from({ length: r }, (_, i) => i);
    const compBinos = compKs.map(k => `\\binom{${n}}{${k}}`).join(" + ");
    const compVals  = compKs.map(k => nCr(n, k)).join(" + ");

    if (directLen <= compKs.length) {
      aCountLatex = `|\\mathrm{A}| = \\sum_{k=${r}}^{${n}} \\binom{${n}}{k} = ${directBinos} = ${directVals} = ${num}`;
    } else {
      aCountLatex = `|\\mathrm{A}| = 2^{${n}} - \\sum_{k=0}^{${r-1}} \\binom{${n}}{k} = ${den} - (${compBinos}) = ${den} - (${compVals}) = ${num}`;
    }
  } else if (modeKey === "AT_MOST") {
    const directLen = r + 1;
    const compKs = Array.from({ length: n - r }, (_, i) => r + 1 + i);
    const compBinos = compKs.map(k => `\\binom{${n}}{${k}}`).join(" + ");
    const compVals  = compKs.map(k => nCr(n, k)).join(" + ");

    if (directLen <= compKs.length) {
      aCountLatex = `|\\mathrm{A}| = \\sum_{k=0}^{${r}} \\binom{${n}}{k} = ${directBinos} = ${directVals} = ${num}`;
    } else {
      aCountLatex = `|\\mathrm{A}| = 2^{${n}} - \\sum_{k=${r+1}}^{${n}} \\binom{${n}}{k} = ${den} - (${compBinos}) = ${den} - (${compVals}) = ${num}`;
    }
  } else if (modeKey === "BETWEEN") {
    const directLen = b - a + 1;
    const compLen = a + (n - b);

    if (directLen <= compLen || a === 0 || b === n) {
      aCountLatex = `|\\mathrm{A}| = \\sum_{k=${a}}^{${b}} \\binom{${n}}{k} = ${directBinos} = ${directVals} = ${num}`;
    } else {
      const compKsLeft  = Array.from({ length: a }, (_, i) => i);
      const compKsRight = Array.from({ length: n - b }, (_, i) => b + 1 + i);
      const leftBinos = compKsLeft.map(k => `\\binom{${n}}{${k}}`).join(" + ") || "0";
      const rightBinos = compKsRight.map(k => `\\binom{${n}}{${k}}`).join(" + ") || "0";
      const leftVals = compKsLeft.map(k => nCr(n, k)).join(" + ") || "0";
      const rightVals = compKsRight.map(k => nCr(n, k)).join(" + ") || "0";
      const innerBinos = (leftBinos === "0" ? "" : leftBinos) + ((leftBinos !== "0" && rightBinos !== "0") ? " + " : "") + (rightBinos === "0" ? "" : rightBinos);
      const innerVals  = (leftVals === "0" ? "" : leftVals) + ((leftVals !== "0" && rightVals !== "0") ? " + " : "") + (rightVals === "0" ? "" : rightVals);

      aCountLatex = `|\\mathrm{A}| = 2^{${n}} - \\Big(${innerBinos}\\Big) = ${den} - (${innerVals}) = ${num}`;
    }
  }

  const probLine = `P(\\mathrm{A}) = \\dfrac{|\\mathrm{A}|}{|\\Omega|} = \\dfrac{${num}}{${den}} ${tail} \\approx ${(num/den).toFixed(4)}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1 — przestrzeń zdarzeń</strong>
        <BlockMath math={omegaLine} />
      </div>

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2 — liczymy liczność zdarzenia A</strong>
        <BlockMath math={aCountLatex} />
      </div>

      <div>
        <strong>Krok 3 — prawdopodobieństwo</strong>
        <BlockMath math={probLine} />
      </div>
    </div>
  );
}